import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react' 
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { Fade } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import './proyects.scss'

function importAll(r) {
  return r.keys().map(r)
} 

// UX notes: cards with clear CTAs, keyboard focusable, quick search, tag filters, lightweight placeholders for thumbnails

export default function Proyects(props) {
  const [projects, setProjects] = useState([])
  const [query, setQuery] = useState('')
  const [input, setInput] = useState('')
  const [activeTag, setActiveTag] = useState('')
  const [selected, setSelected] = useState(null)
  const timeoutRef = useRef(null)


  useEffect(() => {
    setProjects([
      { title: 'Inventario con Punto de Venta', description: 'Sistema de inventarios y TPV con integración contable y reportes en tiempo real.', url: 'https://dpelos.ingeniosoft.dev', tags: ['comercio', 'erp'] },
      { title: 'Apuestas El Rey', description: 'Plataforma de apuestas con control de integridad, pagos y escalabilidad.', url: 'https://apuestas.ingeniosoft.dev', tags: ['juegos', 'pagos'], firstDate: '2022-10-03', tecnologys: ['nodejs', 'Reactjs'] },
      { title: 'Melocotón - Red Social', description: 'Red social con enfoque en comunidades locales y moderación de contenido.', url: 'https://melocoton.ingeniosoft.dev', tags: ['social', 'móvil'] },
      { title: 'Tigo-Une - Despacho Analytics', description: 'Dashboard operativo y analítica en tiempo real para despacho de campo.', tags: ['analítica', 'empresarial'], firstDate: '2019-11-18', images: importAll(require.context('../../asset/images/experiences/tigoUneDespacho', false, /\.(png|jpe?g|svg)$/)), tecnologys: ['php', 'javascript', 'chartJs', 'Oracle', 'Sql server', 'Mysql'] },
      { title: 'Portal Pines (TicLine)', description: 'Venta de accesos a internet por sesión en zonas rurales.', tags: ['conectividad', 'iot'], firstDate: '2020-09-13', images: importAll(require.context('../../asset/images/experiences/portalPines', false, /\.(png|jpe?g|svg)$/)), tecnologys: ['nodejs', 'Reactjs'] },
      { title: 'NetAdvisor (Data Wifi)', description: 'Producto para monitoreo y decisiones basadas en datos de redes wifi.', url: 'https://ingeniosoft.dev', tags: ['analítica', 'redes'] },
      { title: 'Unidos a un Click', description: 'Plataforma de servicios ciudadanos para gobierno regional.', tags: ['gobierno', 'ciudadanos'], firstDate: '2020-09-07', images: importAll(require.context('../../asset/images/experiences/unidosAUnClick', false, /\.(png|jpe?g|svg)$/)), tecnologys: ['nodejs', 'Reactjs'] },
    ])
  }, [])

  const tags = useMemo(() => {
    const s = new Set()
    projects.forEach(p => (p.tags || []).forEach(t => s.add(t)))
    return Array.from(s)
  }, [projects])

  const filtered = useMemo(() => projects.filter(p => {
    const q = query.trim().toLowerCase()
    if (activeTag && !(p.tags || []).includes(activeTag)) return false
    if (!q) return true
    return p.title.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q) || (p.tags || []).some(t => t.includes(q))
  }), [projects, query, activeTag])

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setQuery(input), 300)
    return () => clearTimeout(timeoutRef.current)
  }, [input])

  const escapeRegExp = useCallback((s = '') => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), [])
  const highlight = useCallback((text = '') => {
    const q = query.trim()
    if (!q) return text
    const re = new RegExp(`(${escapeRegExp(q)})`, 'ig')
    return text.replace(re, '<mark>$1</mark>')
  }, [query, escapeRegExp])


  return (
    <section className="section proyects">
      <div className="container">
        <div className="projects-header">
          <div className="projects-title">
            <h2 className="projects-title__main">Proyectos <span className="projects-title__accent">Destacados</span></h2>
            <p className="projects-title__sub">Trabajos seleccionados con foco en calidad, escalabilidad y experiencia de usuario.</p>
          </div>

          <div className="projects-controls">
            <div className="search-wrap">
              <input id="projects-search" aria-label="Buscar proyectos" placeholder="Buscar proyectos, p. ej. analytics, retail" value={input} onChange={e => setInput(e.target.value)} aria-controls="projects-grid" />
              {input && <button type="button" className="search-clear" onClick={() => { setInput(''); setQuery('') }} aria-label="Limpiar búsqueda">×</button>}
            </div>
            <div className="projects-search-meta" aria-live="polite">{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</div>

            <div className="tags">
              <button className={!activeTag ? 'active' : ''} onClick={() => setActiveTag('')}>Todos</button>
              {tags.map(t => <button key={t} className={activeTag === t ? 'active' : ''} onClick={() => setActiveTag(activeTag === t ? '' : t)}>{t}</button>)}
            </div>
          </div>
        </div>

        <div id="projects-grid" className="projects-grid" role="list">
          {filtered.map((p, i) => (
            <article key={p.title + i} className="project-card" role="listitem" tabIndex={0} onClick={() => setSelected(p)} onKeyDown={(e) => e.key === 'Enter' && setSelected(p)}>
              <div className="card-media" aria-hidden>
                {p.images?.length > 0 ? (
                  <div className="thumb"><img src={p.images[0].default} alt={p.title} /></div>
                ) : (
                  <>
                    <div className="placeholder-gradient" />
                    <div className="card-tags">{(p.tags || []).slice(0,2).map(t => <span key={t} className="tag">{t}</span>)}</div>
                  </>
                )}
                {p.tags && p.images?.length > 0 && <div className="card-tags">{(p.tags || []).slice(0,2).map(t => <span key={t} className="tag">{t}</span>)}</div>}
              </div> 
              <div className="card-body">
                <h3 dangerouslySetInnerHTML={{ __html: highlight(p.title) }} />
                <p className="muted" dangerouslySetInnerHTML={{ __html: highlight(p.description || '') }} />
                {p.tecnologys?.length > 0 && <div className="tech-mini">{(p.tecnologys || []).slice(0,3).map(t => <span key={t} className="tech-mini-item">{t}</span>)}</div>}
                <div className="card-actions">
                  {p.url && <a className='link' href={p.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} aria-label={`Abrir ${p.title} en una nueva pestaña`}>Visitar ↗</a>}
                  <button className='btn-outline' onClick={(e) => { e.stopPropagation(); setSelected(p) }}>Detalles</button>
                </div>
              </div>
            </article>
          ))}
          {filtered.length === 0 && (
            <div className="empty">
              <p>No se encontraron proyectos que coincidan.</p>
              <p>Prueba con alguna etiqueta: {tags.map(t => <button key={t} className="suggest-tag" onClick={() => setActiveTag(t)}>{t}</button>)}</p>
            </div>
          )}
        </div>

        <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
          <DialogContent className="project-dialog">
            {selected && (
              <div className="project-detail">
                <h3>{selected.title}</h3>
                <p>{selected.description}</p>

                {selected.images?.length > 0 && (
                  <div className="slide-container">
                    <Fade
                    arrows
                    >
                      {selected.images.map((img, idx) => (
                        <div className="each-fade" key={idx}>
                          <div className="image-container">
                            <img src={img.default} alt={`${selected.title}-${idx}`} />
                          </div>
                        </div>
                      ))}
                    </Fade>
                  </div>
                )}

                {selected.tags && <div className="detail-tags">{selected.tags.map(t => <span key={t} className='tag'>{t}</span>)}</div>}
                {selected.url && <p><a href={selected.url} target="_blank" rel="noopener noreferrer">Abrir proyecto ↗</a></p>}
                {selected.firstDate && <div><span><strong>Primera versión:</strong> {new Date(selected.firstDate).toLocaleString('es-CO', { timeZone: 'America/Bogota', weekday: "long", month: "long", year: "numeric", day: "numeric" })}</span></div>}
                {selected.tecnologys?.length > 0 && (
                  <div className="project-techs" aria-label="Tecnologías implementadas">
                    <div className="tech-label">Tecnologías</div>
                    <div className="tech-list">{selected.tecnologys.map(t => <span key={t} className="tech">{t}</span>)}</div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog> 
      </div>
    </section>
  )
}